import { useCallback, useEffect, useState } from 'react';

interface SelectableItem {
  key: string;
  interested: boolean;
}

export function useItemSelection<T extends SelectableItem>(items: T[]) {
  const [selected, setSelected] = useState<Record<string, T>>({});

  useEffect(() => {
    setSelected((current) => {
      const fresh = items.filter((item) => item.key in current);
      if (!fresh.length) return current;
      const next = { ...current };
      fresh.forEach((item) => {
        next[item.key] = item;
      });
      return next;
    });
  }, [items]);

  const selectedKeys = Object.keys(selected);
  const allSelected = items.length > 0 && items.every((item) => item.key in selected);
  const allSelectedInterested =
    selectedKeys.length > 0 && selectedKeys.every((key) => selected[key].interested);

  const isSelected = useCallback((key: string) => key in selected, [selected]);

  const toggle = useCallback(
    (item: T) =>
      setSelected((current) => {
        const next = { ...current };
        if (next[item.key]) delete next[item.key];
        else next[item.key] = item;
        return next;
      }),
    [],
  );

  const toggleAll = useCallback(
    () =>
      setSelected((current) => {
        const next = { ...current };
        items.forEach((item) => {
          if (allSelected) delete next[item.key];
          else next[item.key] = item;
        });
        return next;
      }),
    [items, allSelected],
  );

  const clear = useCallback(() => setSelected({}), []);

  return {
    selectedKeys,
    selectedItems: Object.values(selected),
    allSelected,
    allSelectedInterested,
    isSelected,
    toggle,
    toggleAll,
    clear,
  };
}
